/**
 * Le blog. Chaque article a une URL propre (/blog/<slug>) : c'est la seule
 * facon d'etre indexe separement.
 *
 * L'ordre du tableau est l'ordre d'affichage, du plus recent au plus ancien.
 */
import type { Article } from '../types.js';

export const ARTICLES: readonly Article[] = [
  {
    slug: 'quest-ce-que-retusch',
    title: 'Qu’est-ce que Retusch ?',
    date: '2026-08-21',
    summary:
      'Un jeu de dessin en ligne à plusieurs, dans la lignée de Gartic Phone pour la forme, mais dont la mécanique tient de Meccha Chameleon : au lieu de se faire reconnaître, on cherche à disparaître.',
    sections: [
      {
        heading: 'Un jeu de dessin où il ne faut pas être reconnu',
        blocks: [
          'Retusch est un jeu de dessin en ligne pour 2 à 10 joueurs. Chaque manche vous confie une illustration dont une case a été effacée, et vous devez la repeindre pour qu’elle se fonde dans l’œuvre. Ensuite, tout le monde regarde les tableaux retouchés et cherche les cases repeintes.',
          'La particularité tient en une phrase : réussir, c’est ne pas être vu.',
        ],
      },
      {
        heading: 'Ce qu’il partage avec Gartic Phone',
        blocks: [
          'La forme est la même : on ouvre un lien, on tape un pseudo, on joue. Pas de compte, pas d’installation, pas de client à télécharger : le jeu tourne dans le navigateur et une partie se lance en quelques secondes. On invite ses amis en collant un lien dans une conversation, et la table se remplit.',
          'Le rythme aussi est familier : des manches courtes, un chronomètre, une phase de dessin puis une phase où l’on découvre ce que les autres ont produit. Et surtout, cette révélation collective en fin de manche où tout le monde commente ce qu’il voit. C’est le même plaisir de groupe.',
          'Comme dans Gartic Phone, savoir dessiner n’est pas requis. Une case de ciel bien posée bat un chef-d’œuvre qui déborde.',
        ],
      },
      {
        heading: 'Ce qu’il emprunte à Meccha Chameleon',
        blocks: [
          'Sur le fond, en revanche, la mécanique est ailleurs. Dans Meccha Chameleon, jeu en trois dimensions sur Steam, on peint son propre personnage puis on le place dans un décor en espérant que personne ne le distingue de ce qui l’entoure. La victoire vient de la ressemblance et du bon emplacement, pas de l’action.',
          'Retusch reprend cette idée et la ramène à l’image. Vous ne choisissez pas où vous cacher, c’est le jeu qui vous donne la case ; en revanche vous fabriquez entièrement ce qui va s’y trouver. La cachette réussit si le regard glisse dessus sans s’arrêter.',
          'Et comme dans tout jeu de camouflage, la tension vient du double rôle. Vous êtes caché et chasseur en même temps : pendant que vous scrutez le tableau à la recherche d’un aplat suspect, quelqu’un scrute le vôtre.',
        ],
      },
      {
        heading: 'Ce qui n’existe nulle part ailleurs',
        blocks: [
          'Le barème récompense les deux camps de façon opposée. Celui qui cherche marque d’autant plus qu’il trouve vite ; celui qui a dessiné marque d’autant plus que sa case résiste longtemps. Une retouche que personne ne voit rapporte le maximum à son auteur.',
          'Il en découle une façon de jouer très particulière : on ne cherche pas à faire beau, on cherche à faire juste. Prélever la bonne couleur, prolonger une ligne au bon endroit, ne pas laisser un coin vide. Ce sont des gestes d’observation avant d’être des gestes de dessin.',
          'Pour comprendre en pratique, le plus simple est de lire les règles complètes, ou de lancer une partie rapide : elle démarre sans réglage et sans attendre.',
        ],
      },
    ],
  },
  {
    slug: 'comment-etre-imbattable',
    title: 'Comment être imbattable à Retusch ?',
    date: '2026-08-21',
    summary:
      'Les raccourcis clavier, la méthode du général vers le détail, et l’erreur des coins vides : trois choses qui séparent un joueur débutant d’un joueur qu’on ne trouve plus.',
    sections: [
      {
        heading: 'Apprenez les raccourcis, vous doublez votre temps utile',
        blocks: [
          'Deux minutes de dessin, cela passe très vite quand on va chercher chaque outil à la souris. Les raccourcis récupèrent facilement vingt ou trente secondes par manche, et ces secondes-là sont celles que vous passerez sur les détails.',
          {
            list: [
              'B pour le pinceau, P pour le pot de peinture, I pour la pipette, H pour la main.',
              '1, 2 et 3 pour les trois tailles de pinceau, sans passer par la barre d’outils.',
              'Ctrl+Z, ou Cmd+Z sur Mac, pour annuler trait par trait.',
              'Alt maintenu bascule sur la pipette et rend la main à l’outil précédent dès que vous relâchez. C’est le plus utile de tous : on prélève une couleur en pleine action sans jamais changer d’outil.',
            ],
          },
          'Un mot sur les claviers AZERTY : les touches 1, 2 et 3 fonctionnent sans Majuscule, même si votre clavier y écrit &, é et ". Le jeu lit la position de la touche, pas le caractère qu’elle produit.',
        ],
      },
      {
        heading: 'Peignez du général vers le détail',
        blocks: [
          'L’erreur classique du débutant est de commencer par un coin et de le soigner. Le chronomètre tombe, les trois quarts de la case sont restés au fond, et la retouche se voit à dix mètres.',
          'La bonne méthode est l’inverse. Commencez au gros pinceau, ou au pot de peinture, et posez les grandes masses de couleur : le ciel, le mur, l’ombre au sol. En vingt secondes, votre case a déjà la bonne allure générale et ne ressort plus comme un bloc étranger.',
          'Ensuite seulement, réduisez la taille et traitez les transitions : la ligne d’horizon qui traverse, le bord d’un objet, la nuance qui change à mi-hauteur. Si le temps manque, vous aurez perdu du détail, pas la manche.',
          'Prélevez systématiquement vos couleurs à la pipette plutôt que de les choisir dans la palette. Une teinte approchante suffit à trahir une case entière, alors que la teinte exacte la rend invisible.',
        ],
      },
      {
        heading: 'Ne laissez jamais les coins vides',
        blocks: [
          'C’est l’indice numéro un, et celui que les joueurs expérimentés cherchent en premier. Une case dont le centre est bien traité mais dont les quatre angles sont restés à la couleur de fond dessine un carré parfait à l’écran. On n’a même pas besoin de regarder le dessin : la forme géométrique saute aux yeux.',
          'Prenez l’habitude de traiter le pourtour en premier, puis le centre. Un cadre bien fondu pardonne un intérieur approximatif ; l’inverse n’est jamais vrai.',
          'Débordez volontairement sur les bords. Le jeu écrête votre trait à la limite de la case, vous ne risquez donc rien à passer largement au-delà, et c’est le seul moyen d’être certain qu’aucun pixel du fond ne subsiste dans un angle.',
        ],
      },
      {
        heading: 'Et quand c’est à vous de chercher',
        blocks: [
          'Balayez d’abord le tableau entier sans zoomer : une case mal fondue apparaît comme une tache, et le zoom ne sert qu’à confirmer. Suivez ensuite les lignes longues, celles qui traversent l’image : une bordure qui se décale d’un cheveu est presque toujours une retouche.',
          'Enfin, souvenez-vous que vous n’avez droit qu’à une seule erreur par manche. Trois secondes de vérification coûtent moins cher que la manche entière.',
        ],
      },
    ],
  },
  {
    slug: 'jeu-de-dessin-entre-amis',
    title: 'Un jeu de dessin à jouer entre amis',
    date: '2026-08-21',
    summary:
      'Pourquoi Retusch fonctionne bien en petit groupe, comment inviter en collant un lien, et pourquoi un seul joueur suffit à débloquer les modes payants pour toute la table.',
    sections: [
      {
        heading: 'Un jeu qui ne demande rien à personne',
        blocks: [
          'Le plus grand obstacle d’une soirée jeu à distance, c’est l’installation. Un compte à créer, un client à télécharger, une version qui ne correspond pas : la moitié du groupe décroche avant d’avoir joué.',
          'Retusch tourne dans le navigateur. Vos amis cliquent sur un lien, tapent un pseudo et se retrouvent à table. Il n’y a rien à installer, rien à créer, et cela marche aussi bien sur ordinateur que sur téléphone.',
          'Autre chose qui compte en groupe : personne n’est mis de côté parce qu’il ne sait pas dessiner. Le jeu ne demande pas de représenter quoi que ce soit, seulement de faire correspondre une case à ce qui l’entoure. Celui qui dessine mal mais observe bien gagne souvent.',
        ],
      },
      {
        heading: 'Inviter, en pratique',
        blocks: [
          'Créez un salon depuis l’accueil. Décochez la case « salon public » si vous voulez rester entre vous : l’invitation par lien devient alors le seul chemin d’entrée.',
          'Copiez le lien avec le bouton prévu, puis collez-le dans votre conversation de groupe, votre salon vocal ou votre messagerie. Il contient le code du salon : vos amis atterrissent directement à votre table.',
          'Vous pouvez régler la partie pendant que le monde arrive : le mode, le nombre de manches, le temps de dessin, la taille de la case et même les catégories d’images. Rien n’est figé tant que vous n’avez pas lancé.',
        ],
      },
      {
        heading: 'Un seul paie, tout le monde joue',
        blocks: [
          'Deux modes sont gratuits pour toujours : la chasse commune, qui est le mode par défaut, et le mode diffusion.',
          'Les autres deviendront payants, mais seulement à la CRÉATION d’une partie. Les rejoindre restera gratuit, sans condition. Concrètement, il suffit qu’une seule personne du groupe ait débloqué le jeu pour que toute la table profite de tous les modes, sans compte et sans rien payer.',
          'Le déblocage est un achat unique, sans abonnement.',
        ],
      },
      {
        heading: 'Quel mode pour quel groupe',
        blocks: [
          'À trois ou quatre, la chasse commune suffit et se comprend en une manche. À partir de six, elle devient franchement bonne : les découvertes s’enchaînent et l’écran bouge en permanence.',
          'Pour finir une soirée, essayez le battle royale : la table se vide manche après manche et la dernière se joue à deux. Pour un groupe mélangé, où certains veulent dessiner tranquillement, le concours retire toute la pression du chronomètre de traque.',
        ],
      },
    ],
  },
  {
    slug: 'jeu-de-dessin-en-stream',
    title: 'Un jeu de dessin à jouer en stream',
    date: '2026-08-21',
    summary:
      'Retusch a un mode conçu pour le direct, où le streamer cherche seul pendant que son public se cache. Comment l’utiliser, et quels autres modes fonctionnent devant une audience.',
    sections: [
      {
        heading: 'Un mode fait pour ça',
        blocks: [
          'Le mode diffusion inverse les rôles. Tous les joueurs dessinent sur le même tableau, sauf l’hôte, qui ne dessine pas du tout. Puis l’hôte est le seul à chercher, pendant que son public le regarde.',
          'C’est exactement la situation qu’un direct réclame, et pour une raison précise : comme l’hôte ne dessine pas, sa propre cachette n’existe pas, et le chat n’a donc rien vu qu’il ne devrait pas voir. Les spectateurs connaissent en revanche les cachettes des autres joueurs, et peuvent le regarder passer à côté trois fois de suite.',
          'Deux règles ont été écrites pour ce cas. Le streamer ne marque aucun point et n’apparaît pas au classement : la partie se joue entre ceux qui se cachent, lui l’anime. Et il peut se tromper autant de fois qu’il le veut, alors que tous les autres modes s’arrêtent à la première erreur. Un direct ne peut pas se terminer sur un clic malheureux.',
          'Le mode diffusion est gratuit, et le restera.',
        ],
      },
      {
        heading: 'Le concours, la deuxième bonne idée',
        blocks: [
          'Dans le mode concours, tout le monde repeint la même case du même tableau, puis chacun vote pour la retouche qu’il préfère. Il n’y a plus rien à chercher, seulement à juger.',
          'Devant une audience, cela fonctionne très bien, et pour la même raison que le mode diffusion : il n’y a rien à trouver, donc rien que le chat puisse éventer. Les propositions se comparent côte à côte, le vote donne un moment de suspense, et la révélation des noms fournit une conclusion nette. C’est aussi le mode le plus court.',
        ],
      },
      {
        heading: 'Et les autres modes',
        blocks: [
          'Rien n’oblige à jouer un mode dédié, mais mieux vaut alors jouer avec des amis plutôt qu’avec le chat. Dans tous les autres modes, le streamer dessine sa propre case à l’écran : les spectateurs la voient se construire et connaissent la réponse avant même que la traque ne commence.',
          'Une partie entre amis se suit en revanche très bien de l’extérieur : la phase de dessin montre chacun construire sa cachette, et la révélation dévoile toutes les cases d’un coup, avec le nom de leur auteur.',
        ],
      },
      {
        heading: 'Faire entrer le chat dans la partie',
        blocks: [
          'Le lien d’invitation est la seule chose à partager. Collez-le dans le chat, et les premiers arrivés remplissent la table : il n’y a ni compte à créer, ni téléchargement, ni file d’attente.',
          'Le plafond d’effectif se règle dans le salon, entre 2 et 10 joueurs. Sur une audience nombreuse, ouvrez plutôt plusieurs parties courtes qu’une seule longue : une manche dure moins de trois minutes.',
        ],
      },
    ],
  },
  {
    slug: 'nouveautes',
    title: 'Les nouveautés de Retusch',
    date: '2026-08-21',
    summary:
      'Les changements de jeu apportés à Retusch, du plus récent au plus ancien : nouveaux modes, nouvelles règles, améliorations du dessin et de la traque.',
    sections: [
      {
        heading: 'Août 2026',
        blocks: [
          {
            list: [
              'La case à repeindre ne part plus d’un carré blanc, mais de la couleur dominante de ce qu’elle recouvre. On ne perd plus la moitié du temps à repeindre un fond qui n’existe pas dans le tableau.',
              'Le pot de peinture ne laisse plus de liseré clair le long des traits : il se glisse désormais sous leur bord adouci.',
              'Le battle royale ne compte plus de points. Le premier joueur découvert quitte la partie, et chacun reçoit un rang de manche affiché sur sa case, du rouge au vert.',
              'Le mode diffusion laisse au streamer autant d’essais qu’il le souhaite : son premier raté ne clôt plus la manche.',
              'En mode spectateur, on peut maintenant se déplacer et zoomer dans le tableau que l’on regarde se construire.',
              'Les réactions emoji partent du pseudo de leur auteur, dans la liste des joueurs, au lieu de monter du bas de l’écran.',
              'Hors de la case à repeindre, le pinceau et le pot déplacent la vue : plus besoin de prendre l’outil main pour se déplacer.',
              'Le nom du jeu, en haut à gauche pendant une partie, ramène à l’accueil après confirmation.',
              'Le japonais et le coréen rejoignent les quinze langues déjà disponibles.',
            ],
          },
        ],
      },
      {
        heading: 'Juillet 2026',
        blocks: [
          {
            list: [
              'Nouveau mode concours : tout le monde repeint la même case, puis chacun vote pour la retouche qu’il préfère.',
              'Nouveau mode diffusion, pensé pour le direct : l’hôte cherche seul pendant que son public se cache.',
              'Les catégories d’images remplacent les niveaux de difficulté. On coche celles que l’on veut voir apparaître, et chaque catégorie montre un tableau d’exemple.',
              'Deux manches d’affilée ne tombent plus sur le même tableau, et les catégories cochées alternent d’une manche à l’autre.',
              'La galerie de fin de partie permet de rejouer une retouche trait par trait, et de l’exporter en image ou en GIF animé.',
            ],
          },
        ],
      },
    ],
  },
];

export function articleBySlug(slug: string): Article | null {
  return ARTICLES.find((article) => article.slug === slug) ?? null;
}

export const BLOG_INDEX = {
  title: 'Le blog de Retusch',
  summary:
    'Articles, conseils de jeu et nouveautés de Retusch, le jeu de dessin en ligne où il faut se fondre dans le tableau plutôt que se faire reconnaître.',
} as const;
